// ============================================
// HEROSHHI - RESET PASSWORD (the link from the email)
// File: src/pages/auth/ResetPasswordPage.jsx
// Same request and redirect as before. The checklist now matches what the
// form actually requires: 8+ characters, a letter and a number.
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../config';
import AuthLayout, { Field, Notice, PasswordInput, PasswordChecklist, SubmitButton, passwordOk } from './AuthLayout';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    if (!token) setTokenValid(false);
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setTokenValid(false); return; }
    if (!passwordOk(formData.password)) {
      setError('Your new password needs at least 8 characters, with a letter and a number.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('The two passwords are different. Type the same one in both boxes.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: formData.password })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/login?reset=success'), 3000);
      } else {
        if (data.message?.includes('expired') || data.message?.includes('invalid')) setTokenValid(false);
        setError(data.message || 'We couldn\'t change your password. Please try again.');
      }
    } catch (err) {
      setError('We couldn\'t reach Heroshhi. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <AuthLayout>
        <h1>This link has expired</h1>
        <p className="hs-auth-sub">Links to reset your password work for one hour, and only once. Ask for a new one, and it arrives in a minute.</p>
        <Link to="/forgot-password" className="hs-btn" style={{ textDecoration: 'none' }}>Send me a new link</Link>
        <p className="hs-auth-foot"><Link to="/login">Back to sign in</Link></p>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <h1>Password changed</h1>
        <p className="hs-auth-sub">You can sign in with your new password now. Taking you there in a moment.</p>
        <Link to="/login?reset=success" className="hs-btn" style={{ textDecoration: 'none' }}>Sign in now</Link>
      </AuthLayout>
    );
  }

  const pw = formData.password, cf = formData.confirmPassword;
  const mismatch = cf && pw !== cf;
  return (
    <AuthLayout>
      <h1>Choose a new password</h1>
      <p className="hs-auth-sub">You'll use it to sign in from now on.</p>
      {error && <Notice>{error}</Notice>}
      <form onSubmit={handleSubmit}>
        <Field id="password" label="New password">
          <PasswordInput id="password" name="password" value={pw} onChange={handleChange}
            placeholder="Choose a password" autoComplete="new-password" autoFocus />
          <PasswordChecklist password={pw} />
        </Field>
        <Field id="confirmPassword" label="Type it again"
          hint={cf ? (mismatch ? 'The two passwords are different.' : 'They match.') : null}
          hintKind={mismatch ? 'bad' : 'ok'}>
          <PasswordInput id="confirmPassword" name="confirmPassword" value={cf} onChange={handleChange}
            placeholder="The same password" autoComplete="new-password" bad={mismatch} />
        </Field>
        <SubmitButton loading={loading} loadingText="Saving" disabled={!pw || !cf || mismatch}>Save new password</SubmitButton>
      </form>
      <p className="hs-auth-foot">Remembered it? <Link to="/login">Sign in</Link></p>
    </AuthLayout>
  );
};

export default ResetPasswordPage;